import { Component, OnInit, Inject, OnDestroy } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { AgentListComponent, TEST_AGENT_INFO } from '@nrwl/test-setup';
import {
  EntityCacheProvider,
  TestServerConfigService,
  DateFormatterService,
  TimeoutWrapperService,
  ApplicationsService,
  SCRIPT_PRECHECK_STATUS_MESSAGE,
  TemporaryScriptSuffix,
  TestTypeValue,
  DevicesService,
  AppsMultiStepSetupService,
  RunTypeValue
} from '@nrwl/common-lib';

@Component({
  selector: 'nrwl-precheck-script-dialog',
  templateUrl: './precheck-script-dialog.component.html',
  styleUrls: ['./precheck-script-dialog.component.scss']
})
export class PrecheckScriptDialogComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line
  dialogRef: MatDialogRef<any>;
  selectedDevices = [];
  selectedAgent = [];
  // eslint-disable-next-line
  appData: any;
  testName: string;
  pollTimeInterval = 5000; //5 secs poll time
  showPrecheckStatus = false;
  scriptStatusMessage: string;
  scriptFailureReason: string;
  precheckFailed = false;
  testType = TestTypeValue.MULTISTEP;
  testInfo: any;
  agentTypes: Array<string>;
  showProgress = false;
  private readonly unsubscribe: Subject<void> = new Subject();
  //eslint-disable-next-line
  private timeoutPromise: any;
  constructor(
    private dialog: MatDialog,
    private preCheckDialogRef: MatDialogRef<PrecheckScriptDialogComponent>,
    private readonly multiStepService: AppsMultiStepSetupService,
    readonly entityCache: EntityCacheProvider,
    readonly serverConfigService: TestServerConfigService,
    private readonly timeout: TimeoutWrapperService,
    private readonly dateFormatterService: DateFormatterService,
    private readonly applicationService: ApplicationsService,
    private readonly deviceService: DevicesService,
    // eslint-disable-next-line
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.testName = this.getTestName();
    this.testInfo = TEST_AGENT_INFO[this.testType];
    this.agentTypes = this.testInfo[0].agentTypes;
    this.appData = this.entityCache.findEntityCache('application').findById(this.data.appId);
  }

  ngOnDestroy(): void {
    if (this.timeoutPromise) {
      this.timeout.cancel(this.timeoutPromise);
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  selectAgent(): void {
    this.dialogRef = this.dialog.open(AgentListComponent, {
      width: '1000px',
      height: '660px',
      disableClose: true,
      data: {
        singleSelection: true,
        testType: TestTypeValue.MULTISTEP,
        agentTypes: this.agentTypes,
        selectedDevices: this.selectedDevices
      }
    });
    this.dialogRef.afterClosed().subscribe(selectedRows => {
      this.showSelectedAgent(selectedRows);
    });
  }
  //eslint-disable-next-line
  closeDialog(closeItem: any): void {
    if (this.showPrecheckStatus && this.showProgress) {
      //dialog closed. check the precheck status in background and show in toast message.
      const params = {
        id: this.appData.getId(),
        name: this.data.scriptName
      };
      this.applicationService.startPollingScriptStatus(params, true);
    }
    this.preCheckDialogRef.close({ selectedAgent: this.selectedDevices, ...closeItem });
  }

  //eslint-disable-next-line
  showSelectedAgent(selectedRows: Array<any>): void {
    if (selectedRows?.length) {
      this.selectedDevices = selectedRows;
      this.selectedAgent = cloneDeep(this.selectedDevices);
    }
  }

  checkDisableTest(): boolean {
    return this.selectedAgent.length === 0 || this.selectedAgent?.[0]?.warningMessage;
  }

  startTest(): void {
    const selectedOriginator = this.deviceService.findDeviceByID(this.selectedAgent[0].id);
    this.multiStepService.output = {
      alias: this.testName,
      interval: 1500,
      scriptUrl: `https://${this.serverConfigService.getHostname()}/entity/v1/application/${this.appData.getId()}/script/${
        this.data.scriptName
      }`,
      proxyConfig: [
        {
          key: 'web-proxy',
          value: '{"config":{"useDefault":true,"protocol":null,"address":null,"port":null}}'
        }
      ],
      selectedApp: {
        key: this.appData.getId(),
        value: this.appData.getName(),
        subContent: this.appData.getUrl()
      },
      selectedOriginator: selectedOriginator,
      runOnDemand: RunTypeValue.ON_DEMAND,
      dryrun: true,
      precheck: true,
      tracerouteEnable: false,
      scriptName: this.data.scriptName
    };

    const lmapObject = this.multiStepService.createLMAPObject();
    this.createTest(lmapObject);
  }

  isTemporaryScriptName(): boolean {
    return this.data.scriptName.includes(TemporaryScriptSuffix);
  }

  getTestName(): string {
    const currentTimestamp = new Date().getTime();
    const date = this.dateFormatterService.getTime(currentTimestamp, 'YYYY-MM-DD:HH:mm:ss');
    const scriptName = this.isTemporaryScriptName()
      ? this.data.scriptName
      : this.data.scriptName + TemporaryScriptSuffix;
    return scriptName + date;
  }

  //eslint-disable-next-line
  createTest(payload: any): void {
    this.showProgress = true;
    this.showPrecheckStatus = true;
    this.scriptStatusMessage = SCRIPT_PRECHECK_STATUS_MESSAGE.STARTING;
    const params = {
      id: this.appData.getId(),
      name: this.data.scriptName
    };
    this.multiStepService
      ._saveControllerTest(payload)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        () => {
          this._startPollingScriptStatus(params);
        },
        () => {
          this.precheckFailed = true;
          this.showProgress = false;
          this.scriptStatusMessage = SCRIPT_PRECHECK_STATUS_MESSAGE.ERROR;
        }
      );
  }

  private _startPollingScriptStatus(params): void {
    if (this.timeoutPromise) {
      this.timeout.cancel(this.timeoutPromise);
    }

    this.applicationService
      .getScriptDetails$(params)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response.isPreCheckPassed) {
            this.precheckFailed = false;
            this.showProgress = false;
            this.scriptStatusMessage = SCRIPT_PRECHECK_STATUS_MESSAGE.SUCCESSFULL;
          } else if (response.failurecode) {
            this.precheckFailed = true;
            this.showProgress = false;
            this.scriptStatusMessage = SCRIPT_PRECHECK_STATUS_MESSAGE.ERROR;
            this.scriptFailureReason = response.errorDescription;
          } else {
            this.timeoutPromise = this.timeout.add(() => {
              this._startPollingScriptStatus(params);
            }, this.pollTimeInterval);
          }
        },
        error => {
          //temporary script might be removed, We need to look on actual script
          if (error.status === 404 && this.isTemporaryScriptName()) {
            params.name = params.name.split('_')[0];
            this._startPollingScriptStatus(params);
          } else {
            this.precheckFailed = true;
            this.showProgress = false;
            this.scriptStatusMessage = SCRIPT_PRECHECK_STATUS_MESSAGE.ERROR;
          }
        }
      );
  }
}
